import React, { useState, useEffect, useCallback } from 'react';
import { promoCodeService } from '../services/promo-code.service';
import { PromoCode } from '../types/promo-code.types';
import { OrderListItem } from '../types/order.types';
import { useNotification } from '../contexts/NotificationContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useOrderModal } from '../contexts/OrderModalContext';
import { Modal } from '../components/UI/Modal';
import { formatPrice } from '../config/currency';
import { getImageUrl } from '../config/imageUrl';

export const PromoCodesPage: React.FC = () => {
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditPointsModalOpen, setIsEditPointsModalOpen] = useState(false);
  const [isOrdersHistoryModalOpen, setIsOrdersHistoryModalOpen] = useState(false);
  const [editingPromoCode, setEditingPromoCode] = useState<PromoCode | null>(null);
  const [editPoints, setEditPoints] = useState(0);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [ordersHistory, setOrdersHistory] = useState<OrderListItem[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedPromoForHistory, setSelectedPromoForHistory] = useState<PromoCode | null>(null);
  const { success, error } = useNotification();
  const { t, language } = useLanguage();
  const { openOrderModal } = useOrderModal();

  // Форма для нового промокода
  const [newPromoCode, setNewPromoCode] = useState({
    name: '',
    code: '',
    discount_percent: 0,
    level: 1,
    parent_id: undefined as number | undefined,
  });

  const loadPromoCodes = useCallback(async () => {
    try {
      setLoading(true);
      const data = await promoCodeService.getAllPromoCodes();
      setPromoCodes(data);
    } catch (err) {
      error(t('promoCodes.error'), t('promoCodes.errorLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [error, t]);

  useEffect(() => {
    loadPromoCodes();
  }, [loadPromoCodes]);

  const handleCreatePromoCode = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPromoCode.name || !newPromoCode.code || newPromoCode.discount_percent <= 0) {
      error(t('promoCodes.errorValidation'), t('promoCodes.errorFillAllFields'));
      return;
    }

    if (newPromoCode.discount_percent > 100) {
      error(t('promoCodes.errorValidation'), t('promoCodes.errorDiscountTooHigh'));
      return;
    }

    if (newPromoCode.level === 2 && !newPromoCode.parent_id) {
      error(t('promoCodes.errorValidation'), t('promoCodes.errorFillAllFields'));
      return;
    }

    try {
      await promoCodeService.createPromoCode(newPromoCode);
      success(t('promoCodes.success'), t('promoCodes.successCreated'));
      setNewPromoCode({ name: '', code: '', discount_percent: 0, level: 1, parent_id: undefined });
      setIsModalOpen(false);
      loadPromoCodes();
    } catch (err: any) {
      if (err.response?.status === 409) {
        error(t('promoCodes.error'), t('promoCodes.errorCodeExists'));
      } else {
        error(t('promoCodes.error'), t('promoCodes.errorCreateFailed'));
      }
    }
  };

  const handleToggleStatus = async (id: number, currentStatus: boolean) => {
    try {
      await promoCodeService.updatePromoCodeStatus(id, { is_active: !currentStatus });
      success(
        t('promoCodes.success'),
        currentStatus ? t('promoCodes.successArchived') : t('promoCodes.successActivated')
      );
      loadPromoCodes();
    } catch (err) {
      error(t('promoCodes.error'), t('promoCodes.errorUpdateFailed'));
    }
  };

  const handleCopyCode = async (code: string, id: number) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedId(id);
      success(t('promoCodes.success'), t('promoCodes.successCopied'));
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      error(t('promoCodes.error'), 'Failed to copy');
    }
  };

  const handleOpenEditPoints = (promo: PromoCode) => {
    setEditingPromoCode(promo);
    setEditPoints(promo.points);
    setIsEditPointsModalOpen(true);
  };

  const handleUpdatePoints = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingPromoCode) return;

    if (editPoints < 0) {
      error(t('promoCodes.errorValidation'), t('promoCodes.errorPointsNegative'));
      return;
    }

    try {
      await promoCodeService.updatePromoCodePoints(editingPromoCode.id, editPoints);
      success(t('promoCodes.success'), t('promoCodes.successPointsUpdated'));
      setIsEditPointsModalOpen(false);
      setEditingPromoCode(null);
      loadPromoCodes();
    } catch (err) {
      error(t('promoCodes.error'), t('promoCodes.errorUpdatePointsFailed'));
    }
  };

  const handleViewOrdersHistory = async (promo: PromoCode) => {
    try {
      setLoadingOrders(true);
      setSelectedPromoForHistory(promo);
      const orders = await promoCodeService.getPromoCodeOrders(promo.id);
      setOrdersHistory(orders);
      setIsOrdersHistoryModalOpen(true);
    } catch (err) {
      error(t('promoCodes.error'), t('promoCodes.errorLoadOrdersFailed'));
    } finally {
      setLoadingOrders(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString(language === 'ru' ? 'ru-RU' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const activePromoCodes = promoCodes.filter(p => p.is_active);
  const level1PromoCodes = promoCodes.filter(p => p.is_active && p.level === 1);

  if (loading) {
    return (
      <div className="container-app py-8">
        <div className="text-center text-text-secondary">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="container-app py-8">
      {/* Заголовок и описание */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary mb-2">{t('promoCodes.title')}</h1>
        <p className="text-text-secondary">{t('promoCodes.subtitle')}</p>
      </div>

      {/* Кнопка добавления */}
      <div className="mb-6 flex justify-end">
        <button
          onClick={() => setIsModalOpen(true)}
          className="btn btn-primary"
        >
          {t('promoCodes.addNew')}
        </button>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setNewPromoCode({ name: '', code: '', discount_percent: 0, level: 1, parent_id: undefined });
        }}
        title={t('promoCodes.formTitle')}
        size="lg"
      >
        <form onSubmit={handleCreatePromoCode} className="space-y-4">
          {/* Выбор уровня промокода */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {t('promoCodes.fieldLevel')}
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="level"
                  value="1"
                  checked={newPromoCode.level === 1}
                  onChange={() => setNewPromoCode({ ...newPromoCode, level: 1, parent_id: undefined })}
                  className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                />
                <span className="text-sm text-text-primary">{t('promoCodes.level1')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="level"
                  value="2"
                  checked={newPromoCode.level === 2}
                  onChange={() => setNewPromoCode({ ...newPromoCode, level: 2 })}
                  className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                />
                <span className="text-sm text-text-primary">{t('promoCodes.level2')}</span>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                {t('promoCodes.fieldName')}
              </label>
              <input
                type="text"
                value={newPromoCode.name}
                onChange={(e) => setNewPromoCode({ ...newPromoCode, name: e.target.value })}
                className="input"
                placeholder={t('promoCodes.placeholderName')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                {t('promoCodes.fieldCode')}
              </label>
              <input
                type="text"
                value={newPromoCode.code}
                onChange={(e) =>
                  setNewPromoCode({ ...newPromoCode, code: e.target.value.toUpperCase() })
                }
                className="input"
                placeholder={t('promoCodes.placeholderCode')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                {t('promoCodes.fieldDiscount')}
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={newPromoCode.discount_percent}
                onChange={(e) =>
                  setNewPromoCode({
                    ...newPromoCode,
                    discount_percent: parseFloat(e.target.value) || 0,
                  })
                }
                className="input"
                placeholder="10"
              />
            </div>
          </div>

          {/* Выбор родительского промокода для 2-го уровня */}
          {newPromoCode.level === 2 && (
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                {t('promoCodes.fieldParentPromo')}
              </label>
              {level1PromoCodes.length > 0 ? (
                <select
                  value={newPromoCode.parent_id || ''}
                  onChange={(e) =>
                    setNewPromoCode({
                      ...newPromoCode,
                      parent_id: e.target.value ? parseInt(e.target.value) : undefined,
                    })
                  }
                  className="input"
                >
                  <option value="">{t('promoCodes.selectParentPromo')}</option>
                  {level1PromoCodes.map((promo) => (
                    <option key={promo.id} value={promo.id}>
                      {promo.code} - {promo.name}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="text-sm text-text-secondary bg-background-100 p-3 rounded">
                  {t('promoCodes.noParentAvailable')}
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setIsModalOpen(false);
                setNewPromoCode({ name: '', code: '', discount_percent: 0, level: 1, parent_id: undefined });
              }}
              className="btn btn-secondary"
            >
              {t('promoCodes.cancel')}
            </button>
            <button type="submit" className="btn btn-primary">
              {t('promoCodes.create')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Модальное окно редактирования баллов */}
      <Modal
        isOpen={isEditPointsModalOpen}
        onClose={() => {
          setIsEditPointsModalOpen(false);
          setEditingPromoCode(null);
        }}
        title={t('promoCodes.editPointsTitle')}
        size="md"
      >
        <form onSubmit={handleUpdatePoints} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {t('promoCodes.fieldPoints')}
            </label>
            <input
              type="number"
              min="0"
              step="1"
              value={editPoints}
              onChange={(e) => setEditPoints(parseInt(e.target.value) || 0)}
              className="input"
              placeholder="0"
            />
          </div>

          {editingPromoCode && (
            <div className="text-sm text-text-secondary bg-background-100 p-3 rounded">
              {t('promoCodes.currentPoints')}: <span className="font-semibold">{editingPromoCode.points}</span>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setIsEditPointsModalOpen(false);
                setEditingPromoCode(null);
              }}
              className="btn btn-secondary"
            >
              {t('promoCodes.cancel')}
            </button>
            <button type="submit" className="btn btn-primary">
              {t('promoCodes.save')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Карточки промокодов */}
      {activePromoCodes.length === 0 ? (
        <div className="card text-center py-12 text-text-secondary">
          {t('promoCodes.noActive')}
        </div>
      ) : (
        <div className="space-y-3">
          {activePromoCodes.map((promo) => {
            const isCopied = copiedId === promo.id;

            return (
              <div
                key={promo.id}
                className="card hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  {/* Левая часть: название, уровень, родитель */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-text-primary">
                        {promo.name}
                      </h3>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        promo.level === 1
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {t(promo.level === 1 ? 'promoCodes.level1' : 'promoCodes.level2')}
                      </span>
                    </div>

                    {promo.level === 2 && promo.parent && (
                      <div className="text-sm text-text-secondary">
                        {t('promoCodes.parentPromo')}: <span className="font-mono font-semibold">{promo.parent.code}</span>
                      </div>
                    )}
                  </div>

                  {/* Промокод */}
                  <div className="relative">
                    <div
                      onClick={() => handleCopyCode(promo.code, promo.id)}
                      className="cursor-pointer bg-primary-500 text-white px-4 py-2 rounded-lg font-mono font-bold text-center hover:bg-primary-600 transition-colors"
                    >
                      {promo.code}
                    </div>
                    {isCopied && (
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                        {t('promoCodes.copied')}
                      </div>
                    )}
                  </div>

                  {/* Скидка */}
                  <div className="flex items-center gap-2 px-4 py-2 bg-green-50 rounded-lg">
                    <span className="text-2xl font-bold text-green-600">
                      {promo.discount_percent}%
                    </span>
                  </div>

                  {/* Статистика */}
                  <div className="flex items-center gap-4 text-sm">
                    <div
                      onClick={() => handleOpenEditPoints(promo)}
                      className="flex items-center gap-1 text-amber-700 cursor-pointer hover:bg-amber-50 px-2 py-1 rounded transition-colors"
                      title={t('promoCodes.editPoints')}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                      </svg>
                      <span className="font-semibold">{promo.points}</span>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </div>
                    <div className="flex items-center gap-1 text-text-secondary">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <span className="font-semibold">{promo.orders_count}</span>
                    </div>
                  </div>

                  {/* Кнопки действий */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleViewOrdersHistory(promo)}
                      className="flex items-center gap-2 py-2 px-4 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors text-sm"
                      title={t('promoCodes.viewOrders')}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {t('promoCodes.viewOrders')}
                    </button>
                    <button
                      onClick={() => handleToggleStatus(promo.id, promo.is_active)}
                      className="flex items-center gap-2 py-2 px-4 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors text-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      {t('promoCodes.actionDelete')}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Модальное окно истории заказов */}
      <Modal
        isOpen={isOrdersHistoryModalOpen}
        onClose={() => {
          setIsOrdersHistoryModalOpen(false);
          setSelectedPromoForHistory(null);
          setOrdersHistory([]);
        }}
        title={`${t('promoCodes.ordersHistory')} ${selectedPromoForHistory ? `- ${selectedPromoForHistory.code}` : ''}`}
        size="lg"
      >
        {loadingOrders ? (
          <div className="text-center py-8 text-text-secondary">{t('loading')}</div>
        ) : ordersHistory.length === 0 ? (
          <div className="text-center py-8 text-text-secondary">
            {t('promoCodes.noOrders')}
          </div>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {ordersHistory.map((order) => (
              <div
                key={order.id}
                className="card hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => openOrderModal(order.id)}
              >
                <div className="flex flex-col gap-3">
                  {/* Шапка заказа */}
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-text-primary">
                      {t('orders.orderNumber')} #{order.id}
                    </h3>
                    <div className="text-sm text-text-secondary">
                      {formatDate(order.created_at)}
                    </div>
                  </div>

                  {/* Позиции заказа */}
                  <div className="flex flex-wrap gap-2">
                    {order.items.slice(0, 4).map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 bg-surface-secondary rounded-lg p-2"
                      >
                        {getImageUrl(item.dish_image) ? (
                          <img
                            src={getImageUrl(item.dish_image)!}
                            alt={item.dish_name}
                            className="w-8 h-8 rounded-md object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-md bg-surface-tertiary flex items-center justify-center">
                            <span className="text-text-tertiary text-xs">🍽️</span>
                          </div>
                        )}
                        <div className="flex flex-col">
                          <span className="text-xs text-text-primary font-medium truncate max-w-[100px]">
                            {item.dish_name}
                          </span>
                          <span className="text-xs text-text-secondary">
                            × {item.quantity}
                          </span>
                        </div>
                      </div>
                    ))}
                    {order.items.length > 4 && (
                      <div className="flex items-center justify-center bg-surface-secondary rounded-lg px-2 py-1">
                        <span className="text-xs text-text-secondary">
                          +{order.items.length - 4}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Итого */}
                  <div className="flex justify-between items-center pt-2 border-t border-border-primary">
                    <span className="text-sm text-text-secondary">{t('orders.total')}:</span>
                    <span className="text-lg font-bold text-text-primary">
                      {formatPrice(Number(order.total))}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};
